/* global document */
/* global window */

import React, { useCallback, useContext, useEffect, useRef, useState, startTransition } from 'react';
import { createPortal } from 'react-dom';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import { IoMdClose } from 'react-icons/io';
import { func } from 'prop-types';
import Button from 'react-bootstrap/Button';
import getRecaptchaToken from '../helpers/getRecaptchaToken';
import AppContext from './context/AppContext';

import { ToastContext } from './context/ToastContext';

import '../styles/QuoteForm.css';

function QuoteForm({ handleToggle }) {
    const [container, setContainer] = useState(null);
    const clickOutsideRef = useRef(null);
    const { sendMessage } = useContext(ToastContext);
    const [partNameOrDescription, setPartNameOrDescription] = useState('');
    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [make, setMake] = useState('');
    const [model, setModel] = useState('');
    const [serial, setSerial] = useState('');
    const [notes, setNotes] = useState('');
    const [disableEmail, setDisableEmail] = useState(false);
    const [disableName, setDisableName] = useState(false);
    const [validated, setValidated] = useState(false);
    const { appContext } = useContext(AppContext);

    const fetchUserData = useCallback(async () => {
        try {
            const response = await fetch(new URL('api/user', process.env.APP_URL));
            const { user } = await response.json();

            if (user) {
                const name = `${user.firstName} ${user.lastName ?? ''}`;
                startTransition(() => {
                    setEmail(user.email);
                    setDisableEmail(true);
                    setName(name);
                    if (name) {
                        setDisableName(true);
                    }
                });
            }
        } catch (e) {
            // do nothing.
        }
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;

        if (form.checkValidity() === false) {
            e.preventDefault();
            e.stopPropagation();
            setValidated(true);
            return;
        }

        try {
            const url = new URL('/api/quoteRequests', process.env.APP_URL);
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    partNameOrDescription,
                    name,
                    email,
                    phoneNumber,
                    ...typeof make !== 'undefined' && ({ make }),
                    ...typeof model !== 'undefined' && ({ model }),
                    ...typeof serial !== 'undefined' && ({ serial }),
                    ...typeof notes !== 'undefined' && ({ notes }),
                    token: await getRecaptchaToken(appContext.config.recaptchaSiteKey)
                }),
            });

            const data = await response.json();

            if (data.error) {
                sendMessage(data.error, 'danger');
            }

            if (data.quoteRequest) {
                handleToggle(false);
                setMake('');
                setModel('');
                setSerial('');
                setPartNameOrDescription('');
                setName('');
                setEmail('');
                setPhoneNumber('');
                setNotes('');
                sendMessage('Quote request created', 'success');
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        }
    };

    useEffect(() => {
        startTransition(() => {
            setContainer(typeof window !== 'undefined' ? document.getElementById('root') : null);
        });
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (clickOutsideRef.current && !clickOutsideRef.current.contains(event.target)) {
                handleToggle(false);
            }
        };

        document.addEventListener('click', handleClickOutside);

        return () => document.removeEventListener('click', handleClickOutside);
    }, [clickOutsideRef, handleToggle]);

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    const handlePartNameOrDescriptionChange = useCallback((e) => setPartNameOrDescription(e.target.value), []);
    const handleNameChange = useCallback((e) => setName(e.target.value), []);
    const handleEmailChange = useCallback((e) => setEmail(e.target.value), []);
    const handleMakeChange = useCallback((e) => setMake(e.target.value), []);
    const handleModelChange = useCallback((e) => setModel(e.target.value), []);
    const handleSerialChange = useCallback((e) => setSerial(e.target.value), []);
    const handleNotesChange = useCallback((e) => setNotes(e.target.value), []);
    const handlePhoneNumberChange = useCallback((e) => {
        let value = e.target.value;
        value = value.replace(/\D/g, '');

        // Enforce digit length limit
        const maxDigits = 12;
        value = value.replace(/\D/g, '').slice(0, maxDigits);
        setPhoneNumber(value);
    }, []);

    if (container === null) {
        return null;
    }

    return createPortal(<div
        className={'quote-form'}
    >
        <Container
            ref={clickOutsideRef}
            className={'quote-form-content'}>
            <IoMdClose
                onClick={() => handleToggle(false)}
                className={'quote-form-close-icon'}
                color={'grey'}
                size={'25px'}/>
            <h2>For an even faster service, call us!</h2>
            <h2
                className={'quote-form-phone'}>{appContext.config.themeConfig.quotePhoneNumber}</h2>
            <Form
                className={'quote-form-form'}
                onSubmit={onSubmit}
                noValidate
                validated={validated}
            >
                <Form.Group
                    className={'mb-3'}
                    controlId={'quoteForm.NameInput'}>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                        type={'text'}
                        value={name}
                        onChange={handleNameChange}
                        disabled={disableName}
                        required
                    />
                    <Form.Control.Feedback
                        type={'invalid'}
                    >
                        Invalid name.
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                    className={'mb-3'}
                    controlId={'quoteForm.EmailInput'}>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                        type={'email'}
                        value={email}
                        onChange={handleEmailChange}
                        disabled={disableEmail}
                        required
                    />
                    <Form.Control.Feedback
                        type={'invalid'}
                    >
                        Invalid email.
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                    className={'mb-3'}
                    controlId={'quoteForm.PartNameInput'}>
                    <Form.Label>Part name/Description</Form.Label>
                    <Form.Control
                        type={'text'}
                        value={partNameOrDescription}
                        onChange={handlePartNameOrDescriptionChange}
                        required
                    />
                    <Form.Control.Feedback
                        type={'invalid'}
                    >
                        Invalid part name.
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                    className={'mb-3'}
                    controlId={'quoteForm.PhoneNumber'}>
                    <Form.Label>Phone number</Form.Label>
                    <Form.Control
                        type={'phoneNumber'}
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                    />
                    <Form.Control.Feedback
                        type={'invalid'}
                    >
                        Invalid phone number.
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                    className={'mb-3'}
                    controlId={'quoteForm.MakeInput'}>
                    <Form.Label>Make</Form.Label>
                    <Form.Control
                        type={'text'}
                        value={make}
                        onChange={handleMakeChange}
                    />
                    <Form.Control.Feedback
                        type={'invalid'}
                    >
                        Invalid make.
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                    className={'mb-3'}
                    controlId={'quoteForm.ModalInput'}>
                    <Form.Label>Model</Form.Label>
                    <Form.Control
                        type={'text'}
                        value={model}
                        onChange={handleModelChange}
                    />
                    <Form.Control.Feedback
                        type={'invalid'}
                    >
                        Invalid model.
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                    className={'mb-3'}
                    controlId={'quoteForm.SerialInput'}>
                    <Form.Label>Serial</Form.Label>
                    <Form.Control
                        type={'text'}
                        value={serial}
                        onChange={handleSerialChange}
                    />
                </Form.Group>
                <Form.Group
                    className={'mb-3'}
                    controlId={'quoteForm.SerialInput'}>
                    <Form.Label>Note</Form.Label>
                    <Form.Control
                        type={'test-area'}
                        value={notes}
                        as={'textarea'}
                        rows={2}
                        onChange={handleNotesChange}
                    />
                </Form.Group>
                <Button
                    className={'quote-form-button'}
                    type={'submit'}
                >
                    Submit Quote
                </Button>
            </Form>
        </Container>
    </div>, container);
}


QuoteForm.propTypes = {
    handleToggle: func,
};

export default QuoteForm;